import { createModel } from "@rematch/core"
import { RootModel } from "./index"
import DefaultClient from "apollo-boost"
import createShopifyCartClient from "@shopify/createApolloCartClient"
import { GetCart } from "@shopify/graphql/queries"
import {
  CreateCart,
  AddCartLine,
  RemoveCartLine,
  UpdateCartLine,
  ApplyDiscount,
  RemoveDiscount,
} from "@shopify/graphql/mutations"
import { UserError, CartLineItem, CartData } from "@shopify/types"
import createContentfulClient from "@cms/createApolloClient"
import { GetCheckoutCartItemProductData } from "@cms/graphql/queries"
import {
  ToastProduct,
  CartProducts,
  CartItem,
  AddToCartPayload,
  LocalCheckout,
} from "../types"
import {
  graphQLRequest,
  getMissingCartProductIds,
  formatContentfulCartProductsData,
  buildLocalCheckout,
  createCheckoutLineItem,
} from "../utils"
import { getIn } from "@utils/index"
import { formatCheckoutURL } from "@utils/urls"
import { getInventoryByLocation } from "@utils/api"
import getAvailabilityByRegion from "@utils/getAvailabilityByRegion"
import { formatInventoryData } from "@hooks/useMultiWarehouseInventory"
import { getLocalStorage, writeLocalStorage } from "@utils/storage"
import {
  UTM_PARAMS_KEY,
  VISITOR_INFO_KEY,
  ELEVAR_COOKIES_KEY,
  MIGRATED_TO_CART_API_KEY,
  CUSTOMER_KEY,
  CUSTOMER_CART_KEY,
  CHECKOUT_KEY,
  DISCOUNT_KEY,
  PASSED_DISCOUNT_KEY,
  LAST_PASSED_DISCOUNT_APPLIED_KEY,
} from "@constants/storageKeys"
import * as R from "ramda"

const CHECKOUT_NOTE = "Checkout created via UAG v2"
export const AUTOMATIC_DISCOUNT_APPLICATION = "AutomaticDiscountApplication"
export const DISCOUNT_CODE_APPLICATION = "DiscountCodeApplication"
export const MANUAL_CODE_APPLICATION = "ManualDiscountApplication"
export const SCRIPT_DISCOUNT_APPLICATION = "ScriptDiscountApplication"

export interface CheckoutState {
  migratedToCartAPIChecked: boolean
  loadingCheckoutData: boolean
  checkoutDataFetched: boolean
  checkoutVerified: boolean
  checkoutId: string | null
  data: CartData
  localCheckout: LocalCheckout
  cartProducts: CartProducts
  toastProduct: ToastProduct | null
  showPromoToast: boolean
  showErrorMessage: boolean
}

const checkout = createModel<RootModel>()({
  name: "checkout",
  state: {
    migratedToCartAPIChecked: false,
    loadingCheckoutData: false,
    checkoutDataFetched: false,
    checkoutVerified: false,
    checkoutId: getLocalStorage(CHECKOUT_KEY),
    data: {} as CartData,
    localCheckout: {} as LocalCheckout,
    cartProducts: {} as CartProducts,
    toastProduct: null,
    showPromoToast: false,
    showErrorMessage: false, // Something went wrong. Your cart has been adjusted.
  } as CheckoutState,
  // https://github.com/rematch/rematch/issues/816
  // @ts-ignore
  selectors: (slice) => ({
    quantity() {
      return slice((checkout) => {
        if (!checkout?.localCheckout?.items) {
          return 0
        }

        return checkout.localCheckout.items.reduce((total, item) => {
          return total + item.quantity
        }, 0)
      })
    },

    cartTotal() {
      return slice((checkout) => {
        return checkout.data?.cost?.totalAmount?.amount || "0.00"
      })
    },

    checkoutLink() {
      return slice((checkout) => {
        const checkoutLink = getIn(checkout, "data.checkoutUrl")
        return checkoutLink ? formatCheckoutURL(checkoutLink) : undefined
      })
    },

    couponCodeApplied() {
      return slice((checkout) => {
        const discountCodes = getIn(
          checkout,
          "data.discountCodes",
          []
        ) as Array<any>
        let discountCode
        discountCodes.forEach(({ __typename, applicable, code }) => {
          if (__typename === "CartDiscountCode" && applicable) {
            discountCode = code
          }
        })
        return discountCode
      })
    },

    automaticDiscountApplied() {
      return slice((checkout) => {
        const discountCodes = getIn(
          checkout,
          "data.discountCodes",
          []
        ) as Array<any>
        return discountCodes.find(({ __typename }) => {
          return __typename === "CartAutomaticDiscountAllocation"
        })
      })
    },

    automaticDiscountAllocations() {
      return slice((checkout) => {
        const lines = getIn(checkout, "data.lines.edges", []) as Array<{
          node: CartLineItem
        }>

        return lines.reduce((discountAllocations, { node }) => {
          if (node.discountAllocations?.length) {
            const discount = node.discountAllocations[0]
            const sku = node.merchandise.sku
            discountAllocations[sku] = discount.discountedAmount.amount
          }

          return discountAllocations
        }, {})
      })
    },
  }),
  reducers: {
    setMigratedToCartAPIChecked(
      state: CheckoutState,
      migratedToCartAPIChecked: boolean
    ) {
      return {
        ...state,
        migratedToCartAPIChecked,
      }
    },

    setLoadingCheckoutData(state: CheckoutState, loadingCheckoutData: boolean) {
      return {
        ...state,
        loadingCheckoutData,
      }
    },

    setCheckoutDataFetched(state: CheckoutState, checkoutDataFetched: boolean) {
      return {
        ...state,
        checkoutDataFetched,
      }
    },

    setCheckoutVerified(state: CheckoutState, checkoutVerified: boolean) {
      return {
        ...state,
        checkoutVerified,
      }
    },

    clearCheckout(state: CheckoutState) {
      return {
        ...state,
        checkoutId: null,
        data: {} as CartData,
        localCheckout: {} as LocalCheckout,
      }
    },

    updateCheckout(
      state: CheckoutState,
      cartData: { id: string; data: CartData; localCheckout: LocalCheckout }
    ) {
      return {
        ...state,
        checkoutId: cartData.id,
        data: cartData.data,
        localCheckout: cartData.localCheckout,
      }
    },

    updateCartProducts(state: CheckoutState, data: CartProducts) {
      return {
        ...state,
        cartProducts: {
          ...state.cartProducts,
          ...data,
        },
      }
    },

    updateToastProduct(
      state: CheckoutState,
      toastProduct: ToastProduct | null
    ) {
      return {
        ...state,
        toastProduct: toastProduct,
      }
    },

    updateShowPromoToast(state: CheckoutState, showPromoToast: boolean) {
      return {
        ...state,
        showPromoToast,
      }
    },

    // -> Cart HOC
    setShowErrorMessage(state: CheckoutState, showErrorMessage: boolean) {
      return {
        ...state,
        showErrorMessage,
      }
    },
  },
  effects: (dispatch: any) => ({
    checkIfMigratedToCartAPI() {
      const migratedToCartChecked = getLocalStorage(MIGRATED_TO_CART_API_KEY)

      if (migratedToCartChecked) {
        dispatch.checkout.setMigratedToCartAPIChecked(true)
      } else {
        writeLocalStorage(MIGRATED_TO_CART_API_KEY, true)
        writeLocalStorage(CHECKOUT_KEY, null)
        writeLocalStorage(CUSTOMER_KEY, null)
        writeLocalStorage(CUSTOMER_CART_KEY, null)
        dispatch.checkout.setMigratedToCartAPIChecked(true)
      }
    },

    setCheckoutId(checkoutId) {
      writeLocalStorage(CHECKOUT_KEY, checkoutId)
    },

    clearCheckout() {
      writeLocalStorage(CHECKOUT_KEY, null)
      writeLocalStorage(DISCOUNT_KEY, null)
    },

    async fetchContentfulCartProducts(ids) {
      const client: DefaultClient<any> = createContentfulClient()

      let productVariantData = {}

      if (ids.length > 0) {
        const { data, errors } = await graphQLRequest(client.query, {
          query: GetCheckoutCartItemProductData,
          variables: { ids },
        })

        if (!errors) {
          productVariantData = data
        }
      }

      const formattedData = formatContentfulCartProductsData(productVariantData)
      return formattedData
    },

    async updateCheckoutData(cartData: CartData, rootState) {
      const missingCartProductIds = getMissingCartProductIds(
        cartData,
        rootState.checkout.cartProducts
      )
      let updatedCartProducts = rootState.checkout.cartProducts

      if (missingCartProductIds.length) {
        const formattedData =
          await dispatch.checkout.fetchContentfulCartProducts(
            missingCartProductIds
          )

        updatedCartProducts = {
          ...rootState.checkout.cartProducts,
          ...formattedData,
        }

        dispatch.checkout.updateCartProducts(updatedCartProducts)
      }

      dispatch.checkout.updateCheckout({
        id: cartData.id,
        data: cartData,
        localCheckout: buildLocalCheckout(cartData, updatedCartProducts),
      })
    },

    async fetchCheckout(checkoutId, rootState) {
      dispatch.checkout.setLoadingCheckoutData(true)

      const client: DefaultClient<any> = createShopifyCartClient()
      const { data, errors } = await graphQLRequest(client.query, {
        query: GetCart,
        variables: {
          cartId: checkoutId,
        },
      })

      dispatch.checkout.setLoadingCheckoutData(false)

      if (errors) {
        dispatch.checkout.clearCheckout()
        dispatch.checkout.setCheckoutDataFetched(true)
        return
      }

      const checkout: CartData = getIn(data, "cart", undefined)
      const lines = getIn(checkout, "lines.edges", []) as Array<{
        node: CartLineItem
      }>

      if (checkout && lines.length > 0) {
        const skus = lines.map(({ node: { merchandise } }) => {
          return merchandise.sku
        }) as Array<string>

        if (!rootState.checkout.checkoutVerified && skus.length > 0) {
          const inventoryData = await getInventoryByLocation(skus)
          const formattedInventoryData = inventoryData
            ? formatInventoryData(inventoryData)
            : {}
          const customerLocation = rootState.customer.customerLocationFetched
            ? rootState.customer.customerLocation
            : await dispatch.customer.fetchCustomerLocation()

          const availableLineItems = lines.filter((line) => {
            const availabilityData = getAvailabilityByRegion(
              customerLocation.customerCountryCode,
              line.node.merchandise.sku,
              formattedInventoryData
            )

            const availableQuantity = availabilityData?.quantity || 0
            return availableQuantity
          })

          dispatch.checkout.setCheckoutVerified(true)

          if (lines.length !== availableLineItems.length) {
            dispatch.checkout.clearCheckout()
            dispatch.checkout.setCheckoutDataFetched(true)
            return
          }
        }

        dispatch.checkout.setCheckoutId(checkout.id)
        dispatch.checkout.updateCheckoutData(checkout)

        const passedDiscount = getLocalStorage(PASSED_DISCOUNT_KEY)
        const lastPassedDiscount = getLocalStorage(
          LAST_PASSED_DISCOUNT_APPLIED_KEY
        )

        if (passedDiscount && passedDiscount !== lastPassedDiscount) {
          await dispatch.checkout.updateDiscount(passedDiscount, rootState)
          writeLocalStorage(LAST_PASSED_DISCOUNT_APPLIED_KEY, passedDiscount)
        }
      } else {
        dispatch.checkout.clearCheckout()
      }
      dispatch.checkout.setCheckoutDataFetched(true)
    },

    async createCheckout(cartItem: CartItem, rootState) {
      const checkoutItems = [createCheckoutLineItem(cartItem)]
      dispatch.checkout.updateCartProducts({
        [cartItem.sku]: cartItem,
      })
      dispatch.checkout.setLoadingCheckoutData(true)

      const visitorInfo = getLocalStorage(VISITOR_INFO_KEY)
      const elevarCookies = getLocalStorage(ELEVAR_COOKIES_KEY)
      const passedDiscount = getLocalStorage(PASSED_DISCOUNT_KEY)
      const lastPassedDiscount = getLocalStorage(
        LAST_PASSED_DISCOUNT_APPLIED_KEY
      )
      const noteAttributes = [] as any

      const validElevarCookies = Object.keys(elevarCookies || {}).filter(
        (k) => !!elevarCookies[k]
      )
      validElevarCookies.forEach((k) => {
        noteAttributes.push({
          name: k,
          value: elevarCookies[k],
        })
      })

      const validVisitorInfo = Object.keys(visitorInfo || {}).filter(
        (k) => !!visitorInfo[k]
      )
      if (validVisitorInfo.length) {
        const cleanedVisitorInfo = validVisitorInfo.reduce((obj, k) => {
          obj[k] = visitorInfo[k]
          return obj
        }, {})

        noteAttributes.push({
          name: "_elevar_visitor_info",
          value: JSON.stringify(cleanedVisitorInfo),
        })
      }

      const checkoutVariables: any = {
        lines: checkoutItems,
        note: CHECKOUT_NOTE,
      }

      if (noteAttributes.length) {
        checkoutVariables.customAttributes = {
          note_attributes: noteAttributes,
        }
      }

      const _window = typeof window !== "undefined" && (window as any)

      if (_window && getIn(_window, "glopal.config.target.countryCode")) {
        checkoutVariables.buyerIdentity = {
          countryCode: getIn(_window, "glopal.config.target.countryCode"),
        }
      }

      const client: DefaultClient<any> = createShopifyCartClient()
      const { data, errors } = await graphQLRequest(client.mutate, {
        mutation: CreateCart,
        variables: checkoutVariables,
      })

      dispatch.checkout.setLoadingCheckoutData(false)

      const userErrors: Array<UserError> = getIn(
        data,
        "cartCreate.userErrors",
        []
      )

      if (errors || (userErrors && userErrors.length)) {
        writeLocalStorage(UTM_PARAMS_KEY, {})
        dispatch.checkout.setShowErrorMessage(true)
        return
      }

      const checkout: CartData = getIn(data, "cartCreate.cart")
      dispatch.customer.associateCheckoutId(checkout.id)
      dispatch.checkout.setCheckoutId(checkout.id)
      dispatch.checkout.updateCheckoutData(checkout)

      if (passedDiscount && passedDiscount !== lastPassedDiscount) {
        await dispatch.checkout.updateDiscount(passedDiscount, rootState)
        writeLocalStorage(LAST_PASSED_DISCOUNT_APPLIED_KEY, passedDiscount)
      }
    },

    async updateCheckoutItem(
      {
        checkoutId,
        cartItem,
        lineIndex,
      }: {
        checkoutId: string
        cartItem: CartItem
        lineIndex: number
      },
      rootState
    ) {
      const lines = getIn(rootState, "checkout.data.lines.edges", [])
      let lineItem
      let lineItemId

      if (
        lineIndex > -1 &&
        lines[lineIndex] &&
        lines[lineIndex].node?.merchandise?.id === cartItem.shopifyVariantId
      ) {
        lineItem = lines[lineIndex]
        lineItemId = getIn(lineItem, "node.id", null)
      }

      const client: DefaultClient<any> = createShopifyCartClient()
      const variables: any = {
        cartId: checkoutId,
      }
      let mutation: any

      if (!lineItem) {
        mutation = AddCartLine
        variables.lines = [createCheckoutLineItem(cartItem)]
      } else if (lineItemId && cartItem.quantity === 0) {
        mutation = RemoveCartLine
        variables.lineIds = [lineItemId]
      } else if (lineItemId) {
        mutation = UpdateCartLine
        variables.lines = [
          {
            id: lineItemId,
            quantity: cartItem.quantity,
          },
        ]
      }

      dispatch.checkout.setLoadingCheckoutData(true)

      const { data, errors } = await graphQLRequest(client.mutate, {
        mutation,
        variables,
      })

      dispatch.checkout.setLoadingCheckoutData(false)

      const checkoutErrors: Array<UserError> = getIn(
        data,
        "cartLinesUpdate.userErrors",
        []
      )

      if (errors || (checkoutErrors && checkoutErrors.length)) {
        console.log(checkoutErrors?.length ? checkoutErrors : errors)
        dispatch.checkout.setShowErrorMessage(true)
        return
      }

      const checkout: CartData =
        getIn(data, "cartLinesAdd.cart") ||
        getIn(data, "cartLinesRemove.cart") ||
        getIn(data, "cartLinesUpdate.cart")
      dispatch.checkout.updateCheckoutData(checkout)

      const _window = typeof window !== "undefined" && (window as any)
      let dataLayer = _window?.dataLayer || []
      if (dataLayer) {
        const items = getIn(checkout, "lines.edges", [])
        const formattedItems =
          items
            .filter(({ node }) => !!node)
            .map(({ node }) => ({
              id: node.merchandise?.id,
              pricePerItem: node.cost?.amountPerQuantity?.amount,
              quantity: node.quantity,
            })) || []

        dataLayer.push({
          event: "maestra_set_cart",
          items: formattedItems,
          customerEmail: getIn(rootState, "customer.data.email"),
        })
      }
    },

    async updateDiscount(discountCode: string | undefined, rootState) {
      const client: DefaultClient<any> = createShopifyCartClient()
      const checkoutId = rootState.checkout.checkoutId

      if (discountCode) {
        dispatch.checkout.setLoadingCheckoutData(true)

        const { data, errors } = await graphQLRequest(client.mutate, {
          mutation: ApplyDiscount,
          variables: {
            cartId: checkoutId,
            discountCode,
          },
        })

        dispatch.checkout.setLoadingCheckoutData(false)

        const checkoutErrors: Array<UserError> = getIn(
          data,
          "cartDiscountCodesUpdate.userErrors",
          []
        )

        if (errors || (checkoutErrors && checkoutErrors.length)) {
          return getIn(checkoutErrors, "0", checkoutErrors)
        }

        const checkout: CartData = getIn(data, "cartDiscountCodesUpdate.cart")

        dispatch.checkout.updateCheckoutData(checkout)

        return {}
      } else {
        dispatch.checkout.setLoadingCheckoutData(true)

        const { data, errors } = await graphQLRequest(client.mutate, {
          mutation: RemoveDiscount,
          variables: {
            checkoutId,
          },
        })

        dispatch.checkout.setLoadingCheckoutData(false)

        const checkoutErrors: Array<UserError> = getIn(
          data,
          "cartDiscountCodesUpdate.userErrors",
          []
        )

        if (errors || (checkoutErrors && checkoutErrors.length)) {
          console.log(checkoutErrors?.length ? checkoutErrors : errors)
          dispatch.checkout.setShowErrorMessage(true)
          return
        }

        const checkout: CartData = getIn(data, "cartDiscountCodesUpdate.cart")
        dispatch.checkout.updateCheckoutData(checkout)
      }
    },

    // -> Product Details
    addToCart({ cartProduct, showPromoToast }: AddToCartPayload, rootState) {
      dispatch.checkout.updateCartProducts({
        ...rootState.checkout.cartProducts,
        [cartProduct.sku]: cartProduct,
      })

      if (rootState.checkout.checkoutId) {
        const lines = getIn(rootState, "checkout.data.lines.edges", [])
        const linesWithProduct = lines.filter(
          (item: { node: CartLineItem }) => {
            return item.node.merchandise?.id === cartProduct.shopifyVariantId
          }
        )
        const line =
          linesWithProduct?.length > 1
            ? linesWithProduct.find((item: { node: CartLineItem }) => {
                return item.node.discountAllocations.length === 0
              })
            : linesWithProduct?.length
            ? linesWithProduct[0]
            : null
        const lineIndex = line
          ? lines.findIndex((item: { node: CartLineItem }) => {
              return item.node.id === line.node.id
            })
          : -1
        const quantity = (line && getIn(line, "node.quantity")) || 0

        dispatch.checkout.updateCheckoutItem({
          checkoutId: rootState.checkout.checkoutId,
          cartItem: {
            ...cartProduct,
            quantity: quantity + 1,
          },
          lineIndex,
        })
      } else {
        dispatch.checkout.createCheckout({ ...cartProduct, quantity: 1 })
      }

      if (showPromoToast) {
        dispatch.checkout.updateShowPromoToast(true)
        dispatch.checkout.clearShowPromoToast()
      } else {
        dispatch.checkout.updateToastProduct(
          R.pick(
            [
              "customPrint",
              "productSlug",
              "productName",
              "variantName",
              "variantDevice",
              "device",
              "image",
            ],
            cartProduct
          ) as ToastProduct
        )

        dispatch.checkout.clearToastProduct()
      }
    },

    // -> Cart HOC
    updateCart(
      { cartItem, lineIndex }: { cartItem: CartItem; lineIndex: number },
      rootState
    ) {
      dispatch.checkout.updateCheckoutItem({
        checkoutId: rootState.checkout.checkoutId,
        cartItem,
        lineIndex,
      })
    },

    async clearToastProduct() {
      await new Promise((resolve) => {
        setTimeout(resolve, 5000)
      })

      dispatch.checkout.updateToastProduct(null)
    },

    async clearShowPromoToast() {
      await new Promise((resolve) => {
        setTimeout(resolve, 5000)
      })

      dispatch.checkout.updateShowPromoToast(false)
    },

    // -> Cart HOC
    async clearShowErrorMessage() {
      await new Promise((resolve) => {
        setTimeout(resolve, 5000)
      })

      dispatch.checkout.setShowErrorMessage(false)
    },
  }),
})

export default checkout
